import { Plus, Trash2 } from 'lucide-react'
import type { ExerciseEditorProps } from '@/types'
import { BLANK_MARKER, hasBlankMarker, splitByBlank } from '@/lib/exerciseUtils'

export default function FillBlankEditor({ formData, setFormData, isViewMode }: ExerciseEditorProps) {
  const insertBlank = () => {
    const textarea = document.getElementById('fill-blank-question') as HTMLTextAreaElement
    if (!textarea) return

    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const text = formData.question
    const newText = text.substring(0, start) + BLANK_MARKER + text.substring(end)
    setFormData(prev => ({ ...prev, question: newText }))
  }

  const addAlternative = () => {
    setFormData(prev => ({ ...prev, options: [...prev.options, ''] }))
  }

  const removeAlternative = (index: number) => {
    setFormData(prev => ({ ...prev, options: prev.options.filter((_, i) => i !== index) }))
  }

  const updateAlternative = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.map((o, i) => i === index ? value : o)
    }))
  }

  return (
    <div className="space-y-4">
      {/* Instrucciones */}
      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <p className="text-sm text-amber-800">
          Escribe la oracion y usa <code className="bg-amber-100 px-1 rounded font-mono">{BLANK_MARKER}</code> para marcar
          donde va el espacio en blanco. Ejemplo: "Je {BLANK_MARKER} francais."
        </p>
      </div>

      {/* Oracion con hueco */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label htmlFor="fill-blank-question" className="text-sm font-medium text-gray-700">
            Oracion con Espacio en Blanco
          </label>
          {!isViewMode && (
            <button
              type="button"
              onClick={insertBlank}
              className="text-sm text-[#0055A4] hover:text-[#0055A4] px-2 py-1 bg-[#0055A4]/5 rounded"
            >
              Insertar {BLANK_MARKER}
            </button>
          )}
        </div>
        <textarea
          id="fill-blank-question"
          value={formData.question}
          onChange={(e) => setFormData(prev => ({ ...prev, question: e.target.value }))}
          placeholder={`Escribe la oracion con ${BLANK_MARKER} donde va el espacio...`}
          rows={3}
          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed font-mono"
          required
          disabled={isViewMode}
        />
      </div>

      {/* Vista previa */}
      {hasBlankMarker(formData.question) && (
        <div className="p-4 bg-gray-50 border rounded-lg">
          <p className="text-xs font-medium text-gray-500 mb-2">Vista previa:</p>
          <p className="text-lg text-gray-900">
            {splitByBlank(formData.question).map((part, i, arr) => (
              <span key={i}>
                {part}
                {i < arr.length - 1 && (
                  <span className="inline-block min-w-[80px] border-b-2 border-[#0055A4]/40 mx-1 text-center text-[#0055A4] font-medium">
                    {formData.correct_answer || '?'}
                  </span>
                )}
              </span>
            ))}
          </p>
        </div>
      )}

      {/* Respuesta correcta */}
      <div>
        <label htmlFor="fill-blank-answer" className="block text-sm font-medium text-gray-700 mb-1">
          Respuesta Correcta (la palabra que llena el espacio)
        </label>
        <input id="fill-blank-answer" type="text" value={formData.correct_answer}
          onChange={(e) => setFormData(prev => ({ ...prev, correct_answer: e.target.value }))}
          placeholder="Ej: parle"
          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
          required
          disabled={isViewMode}
        />
      </div>

      {/* Respuestas alternativas */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">
            Respuestas Alternativas Aceptadas (opcional)
          </span>
          {!isViewMode && (
            <button
              type="button"
              onClick={addAlternative}
              className="text-sm text-[#0055A4] hover:text-[#0055A4] flex items-center gap-1"
            >
              <Plus className="w-4 h-4" />
              Agregar
            </button>
          )}
        </div>
        {formData.options.length > 0 && (
          <div className="space-y-2">
            {formData.options.map((alt, index) => (
              <div key={index} className="flex items-center gap-2">
                <input
                  type="text"
                  value={alt}
                  onChange={(e) => updateAlternative(index, e.target.value)}
                  placeholder="Respuesta alternativa..."
                  className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
                  disabled={isViewMode}
                />
                {!isViewMode && (
                  <button
                    type="button"
                    onClick={() => removeAlternative(index)}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
